import { useState } from 'react';
import LogisticServiceDetailsTabs, {
  IlogisticServiceDetailsTab
} from '../LogisticServiceDetailsTabs/LogisticServiceDetailsTabs';
import { getLogisticServicesTrelloDataByState } from '../../../../../../services/services';
import {
  IlogisticServiceDetails,
  IlogisticServiceTrelloCardData,
  LOCATION_PLACE_TYPE,
  LOGISTIC_SERVICE_STATUS_NAME
} from '../../../../../../types/services';
import { displayErrorNotification } from '../../../../../../utils/displayNudosStandardNotifications';
import LogisticServiceHeaderInformationSectionSkeletonLoader from './LogisticServiceHeaderInformationSectionSkeletonLoader';
import LogisticsServicesTrelloModals from '../../../../../services/components/ModalsLogisticsService/LogisticsServicesTrelloModals';
import { ILogisticServiceHistory } from 'types/suscription/history.types';
import { LogisticServiceLastUpdate } from '../LogisticServiceLastUpdate/LogisticServiceLastUpdate';

import './LogisticServiceHeaderInformationSection.scss';

const LogisticServiceHeaderInformationSection = ({
  logisticServiceData,
  logisticServiceHistoryData,
  handleTabSelectionCallback,
  showSkeletonLoader,
  updateDataCallback,
  updateWithoutLoader
}: {
  logisticServiceData: IlogisticServiceDetails;
  logisticServiceHistoryData: ILogisticServiceHistory[];
  handleTabSelectionCallback: (newTabData: IlogisticServiceDetailsTab) => void;
  showSkeletonLoader: boolean;
  updateDataCallback: () => Promise<void>;
  updateWithoutLoader: () => Promise<void>;
}) => {
  const { id, title, statusName, manualQuote } = logisticServiceData;

  // Estados para controlar los modales
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showDelayedModal, setShowDelayedModal] = useState<boolean>(false);
  const [showActiveModal, setShowActiveModal] = useState<boolean>(false);
  const [showDeliveredModal, setShowDeliveredModal] = useState<boolean>(false);
  const [showInProcessModal, setShowInProcessModal] = useState<boolean>(false);
  const [selectedLogisticServiceDataForModal, setSelectedLogisticServiceDataForModal] =
    useState<IlogisticServiceTrelloCardData>();

  /**
   * Maneja el cambio de estado del servicio logístico
   * @param newStatus - Nuevo estado seleccionado
   */
  const validateConfirmedStatus = () => {
    const originViewedChanges = logisticServiceData.addressIssue?.origin?.viewedChanges;
    const destinationViewedChanges = logisticServiceData.addressIssue?.destination?.viewedChanges;
    const originAddressEditedByUser = logisticServiceData.addressIssue?.origin?.editedBy === 'user';
    const destinationAddressEditedByUser = logisticServiceData.addressIssue?.destination?.editedBy === 'user';
    const originAddressEditedByUserAndNotViewed = originAddressEditedByUser && !originViewedChanges;
    const destinationAddressEditedByUserAndNotViewed = destinationAddressEditedByUser && !destinationViewedChanges;

    if (originAddressEditedByUserAndNotViewed || destinationAddressEditedByUserAndNotViewed) {
      displayErrorNotification({
        customJSXMessage: <>No puedes mover el servicio hasta revisar los cambios realizados por el usuario</>
      });
      return false;
    }

    if (!logisticServiceData.quoteDocumentUrl) {
      displayErrorNotification({
        customJSXMessage: <>Se necesita una cotización para mover el servicio logístico a confirmado</>
      });
      return false;
    }

    return true;
  };

  const validateInProcessStatus = () => {
    const originViewedChanges = logisticServiceData.addressIssue?.origin?.viewedChanges;
    const destinationViewedChanges = logisticServiceData.addressIssue?.destination?.viewedChanges;
    const originAddressEdited = logisticServiceData.addressIssue?.origin?.editedBy === 'user';
    const destinationAddressEdited = logisticServiceData.addressIssue?.destination?.editedBy === 'user';

    if ((!originViewedChanges && originAddressEdited) || (!destinationViewedChanges && destinationAddressEdited)) {
      displayErrorNotification({
        customJSXMessage: <>No puedes mover el servicio hasta revisar los cambios realizados por el usuario</>
      });
      return false;
    }

    return true;
  };

  const validateDeliveredStatus = () => {
    const originIsWarehouse = logisticServiceData.originLocation?.place === LOCATION_PLACE_TYPE.NUDOS;
    const destinationIsNotWarehouse = logisticServiceData.destinationLocation?.place !== LOCATION_PLACE_TYPE.NUDOS;
    const hasPendingConfigurations = logisticServiceData.hasPendingConfiguration;

    if (hasPendingConfigurations && originIsWarehouse && destinationIsNotWarehouse) {
      displayErrorNotification({
        customJSXMessage: <>No puedes mover el servicio a entregado hasta completar los servicios adicionales</>,
        button1Text: 'Ir a los servicios adicionales',
        button1Action: () => (window.location.href = `/logistic-service/details/${logisticServiceData.id}?initialTab=2`)
      });
      return false;
    }

    return true;
  };

  const handleStatusChange = async (newStatus: LOGISTIC_SERVICE_STATUS_NAME, isLate?: boolean | null) => {
    const { CANCELLED, CONFIRMED, ACTIVE, DELIVERED, INPROCESS, DELAYED, TOCONFIRMED } = LOGISTIC_SERVICE_STATUS_NAME;

    let enrichedData;
    try {
      if (logisticServiceData.id) {
        const currentStatus = isLate ? DELAYED : statusName?.toLowerCase() || CONFIRMED;
        const [enrichedServiceData] = await getLogisticServicesTrelloDataByState([
          `/logistic-service/${currentStatus}?searchedId=${logisticServiceData.id}`,
          currentStatus
        ]);
        enrichedData = enrichedServiceData;
      }
    } catch (error) {
      enrichedData = null;
    }

    switch (newStatus) {
      case TOCONFIRMED:
      case CONFIRMED:
        if (!validateConfirmedStatus()) return;
        setSelectedLogisticServiceDataForModal(enrichedData as IlogisticServiceTrelloCardData);
        setShowConfirmModal(true);
        break;

      case INPROCESS:
        if (!validateInProcessStatus()) return;
        setSelectedLogisticServiceDataForModal(enrichedData as IlogisticServiceTrelloCardData);
        setShowInProcessModal(true);
        break;

      case ACTIVE:
        setSelectedLogisticServiceDataForModal(enrichedData as IlogisticServiceTrelloCardData);
        setShowActiveModal(true);
        break;

      case DELIVERED:
        if (!validateDeliveredStatus()) return;
        setSelectedLogisticServiceDataForModal(enrichedData as IlogisticServiceTrelloCardData);
        setShowDeliveredModal(true);
        break;

      case CANCELLED:
        setSelectedLogisticServiceDataForModal(enrichedData as IlogisticServiceTrelloCardData);
        setShowCancelModal(true);
        break;

      case DELAYED:
        setSelectedLogisticServiceDataForModal(enrichedData as IlogisticServiceTrelloCardData);
        setShowDelayedModal(true);
        break;

      default:
        break;
    }
  };

  if (showSkeletonLoader) return <LogisticServiceHeaderInformationSectionSkeletonLoader />;

  const clearAllModals = async () => {
    setShowCancelModal(false);
    setShowConfirmModal(false);
    setShowDelayedModal(false);
    setShowActiveModal(false);
    setShowDeliveredModal(false);
    setShowInProcessModal(false);
    setSelectedLogisticServiceDataForModal(undefined);

    await updateDataCallback();
  };

  return (
    <>
      <LogisticsServicesTrelloModals
        selectedLogisticServiceDataForModal={selectedLogisticServiceDataForModal}
        setSelectedLogisticServiceDataForModal={setSelectedLogisticServiceDataForModal}
        closeAndRestoreList={clearAllModals}
        cancelModalData={{
          showModal: showCancelModal,
          closeModal: clearAllModals
        }}
        confirmModalData={{
          showModal: showConfirmModal,
          closeModal: clearAllModals
        }}
        processModalData={{
          showModal: showInProcessModal,
          closeModal: clearAllModals
        }}
        delayedModalData={{
          showModal: showDelayedModal,
          closeModal: clearAllModals
        }}
        deliveredModalData={{
          showModal: showDeliveredModal,
          closeModal: clearAllModals
        }}
        activeModalData={{
          showModal: showActiveModal,
          closeModal: clearAllModals
        }}
      />
      <div className="LogisticServiceHeaderInformationSection">
        <div className="leftContent">
          <div className="logisticServiceTitleAndId">
            <div className="logisticNumber">SL Nº{id || ''}</div>
            <div className="logisticTitle">{title}</div>
            {manualQuote ? '🤚' : '🔁'}
          </div>
          <LogisticServiceDetailsTabs
            logisticServiceData={logisticServiceData || {}}
            logisticServiceHistoryData={logisticServiceHistoryData}
            handleTabSelectionCallback={handleTabSelectionCallback}
            updateDataCallback={updateDataCallback}
            updateWithoutLoader={updateWithoutLoader}
          />
        </div>
        <div className="rightContent">
          <LogisticServiceLastUpdate
            statusName={(statusName as LOGISTIC_SERVICE_STATUS_NAME) ?? LOGISTIC_SERVICE_STATUS_NAME.TOCONFIRMED}
            logisticServiceHistoryData={logisticServiceHistoryData}
            onStatusChange={handleStatusChange}
            isLate={logisticServiceData?.isLate || logisticServiceData?.delayed}
          />
        </div>
      </div>
    </>
  );
};

export default LogisticServiceHeaderInformationSection;
